"use strict";
/**
 * Order Page Handler
 *
 * Thin coordinator that wires up all order-page UI by delegating to
 * focused submodules under `./order/`. Each submodule owns a single
 * concern (slippage, manual price, baseball, navigation, etc.) and
 * can be imported independently for testing or reuse.
 *
 * Submodules:
 * - order-placement   – core order execution + error handling
 * - two-stage-click   – click-to-arm, click-to-confirm UX
 * - slippage-controls – unified & independent slippage sliders
 * - manual-price-controls – manual Yes/No price inputs
 * - baseball-controls – baseball mode buttons, factor controls, scoreboard
 * - basketball/order-buttons – basketball mode buttons + pricing controls
 * - soccer/order-buttons – soccer mode buttons + pricing controls
 * - market-navigation – back, settings, market switcher, toggles
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.setupOrderPage = setupOrderPage;
const state_1 = require("../state");
const ui_1 = require("../ui");
const index_1 = require("./order/index");
const modal_handlers_1 = require("./modal-handlers");
const additional_markets_service_1 = require("../services/additional-markets-service");
/**
 * Initialise all event listeners and UI state for the order page.
 *
 * Called once from `initApp` in the application entry point.
 */
function setupOrderPage() {
    // -----------------------------------------------------------------------
    // DOM element references
    // -----------------------------------------------------------------------
    /** Standard Buy/Sell order buttons */
    const buttons = {
        buyA: document.getElementById("buy-option-a"),
        sellA: document.getElementById("sell-option-a"),
        buyB: document.getElementById("buy-option-b"),
        sellB: document.getElementById("sell-option-b"),
    };
    const allButtons = Object.values(buttons).filter((b) => b !== null);
    /** Baseball-mode order buttons (offense) */
    const bbButtons = {
        miscTopA: document.getElementById("bb-misc-top-a"),
        extA: document.getElementById("bb-ext-off-a"),
        singleA: document.getElementById("bb-single-a"),
        dblA: document.getElementById("bb-dbl-a"),
        trplA: document.getElementById("bb-trpl-a"),
        hrA: document.getElementById("bb-hr-a"),
        miscBottomA: document.getElementById("bb-misc-bottom-a"),
        miscTopB: document.getElementById("bb-misc-top-b"),
        extB: document.getElementById("bb-ext-off-b"),
        singleB: document.getElementById("bb-single-b"),
        dblB: document.getElementById("bb-dbl-b"),
        trplB: document.getElementById("bb-trpl-b"),
        hrB: document.getElementById("bb-hr-b"),
        miscBottomB: document.getElementById("bb-misc-bottom-b"),
        sellA: document.getElementById("bb-sell-a"),
        sellB: document.getElementById("bb-sell-b"),
    };
    /** Baseball-mode defense buttons (outs) */
    const bbDefButtons = {
        miscTopA: document.getElementById("bb-misc-def-top-a"),
        extA: document.getElementById("bb-ext-def-a"),
        koA: document.getElementById("bb-ko-a"),
        outA: document.getElementById("bb-out-a"),
        dpA: document.getElementById("bb-dp-a"),
        tpA: document.getElementById("bb-tp-a"),
        miscBottomA: document.getElementById("bb-misc-def-bottom-a"),
        miscTopB: document.getElementById("bb-misc-def-top-b"),
        extB: document.getElementById("bb-ext-def-b"),
        koB: document.getElementById("bb-ko-b"),
        outB: document.getElementById("bb-out-b"),
        dpB: document.getElementById("bb-dp-b"),
        tpB: document.getElementById("bb-tp-b"),
        miscBottomB: document.getElementById("bb-misc-def-bottom-b"),
    };
    /** Basketball-mode order buttons */
    const bkButtons = {
        miscA: document.getElementById("bk-misc-a"),
        extA: document.getElementById("bk-ext-a"),
        twoPtrA: document.getElementById("bk-2ptr-a"),
        threePtrA: document.getElementById("bk-3ptr-a"),
        miscB: document.getElementById("bk-misc-b"),
        extB: document.getElementById("bk-ext-b"),
        twoPtrB: document.getElementById("bk-2ptr-b"),
        threePtrB: document.getElementById("bk-3ptr-b"),
        sellA: document.getElementById("bk-sell-a"),
        sellB: document.getElementById("bk-sell-b"),
    };
    /** Soccer-mode order buttons */
    const scButtons = {
        miscA: document.getElementById("sc-misc-a"),
        extA: document.getElementById("sc-ext-a"),
        goalMaybeA: document.getElementById("sc-goal-maybe-a"),
        goalA: document.getElementById("sc-goal-a"),
        redA: document.getElementById("sc-red-a"),
        miscB: document.getElementById("sc-misc-b"),
        extB: document.getElementById("sc-ext-b"),
        goalMaybeB: document.getElementById("sc-goal-maybe-b"),
        goalB: document.getElementById("sc-goal-b"),
        redB: document.getElementById("sc-red-b"),
        sellA: document.getElementById("sc-sell-a"),
        sellB: document.getElementById("sc-sell-b"),
    };
    /**
     * All sport-layout buttons form one mutual-exclusion / lock-toggle group:
     * only one sport layout is ever visible, so a combined group is correct.
     */
    const sportButtons = [
        ...Object.values(bbButtons),
        ...Object.values(bbDefButtons),
        ...Object.values(bkButtons),
        ...Object.values(scButtons),
    ].filter((b) => b !== null);
    // Shared two-stage-click state
    const armTimeoutId = { value: null };
    const confirmRequired = { value: true };
    // -----------------------------------------------------------------------
    // Toggle label helpers (read from DOM + state)
    // -----------------------------------------------------------------------
    const confirmToggleBtn = document.getElementById("toggle-confirm-btn");
    const delayedPriceToggleBtn = document.getElementById("toggle-delayed-price-btn");
    const updateConfirmToggle = () => {
        if (confirmToggleBtn) {
            confirmToggleBtn.textContent = confirmRequired.value
                ? "2C: On"
                : "2C: Off";
            confirmToggleBtn.setAttribute("aria-pressed", confirmRequired.value ? "true" : "false");
        }
        // Warn (red border) on every order button when the 2-click guard is OFF,
        // so it's obvious that a single tap fires an order. This is the single
        // render point for confirm state: it runs on init and after every toggle.
        const guardOff = !confirmRequired.value;
        for (const btn of [...allButtons, ...sportButtons]) {
            btn?.classList.toggle("confirm-guard-off", guardOff);
        }
    };
    const updateDelayedPriceToggle = () => {
        if (!delayedPriceToggleBtn)
            return;
        const state = (0, state_1.getState)();
        const isDelayed = state.tradingSettings.useDelayedPrice === true;
        delayedPriceToggleBtn.textContent = isDelayed ? "Dly" : "T0";
        delayedPriceToggleBtn.setAttribute("aria-pressed", isDelayed ? "true" : "false");
    };
    // -----------------------------------------------------------------------
    // 1. Market navigation, toggles, and page-level controls
    // -----------------------------------------------------------------------
    (0, index_1.setupMarketNavigation)({
        allButtons,
        baseballButtons: sportButtons,
        armTimeoutId,
        confirmRequired,
        updateConfirmToggle,
        updateDelayedPriceToggle,
    });
    // -----------------------------------------------------------------------
    // 2. Slippage controls (unified + independent)
    // -----------------------------------------------------------------------
    (0, index_1.setupUnifiedSlippageControls)(document.getElementById("slippage-slider"), document.getElementById("slippage-decrement-btn"), document.getElementById("slippage-increment-btn"));
    (0, index_1.setupIndependentSlippageControls)({
        independentSlippageToggle: document.getElementById("independent-slippage-toggle"),
        independentSlippageToggleBack: document.getElementById("independent-slippage-toggle-back"),
        slippageSliderA: document.getElementById("slippage-slider-a"),
        slippageSliderB: document.getElementById("slippage-slider-b"),
        slippageADecrementBtn: document.getElementById("slippage-a-decrement-btn"),
        slippageAIncrementBtn: document.getElementById("slippage-a-increment-btn"),
        slippageBDecrementBtn: document.getElementById("slippage-b-decrement-btn"),
        slippageBIncrementBtn: document.getElementById("slippage-b-increment-btn"),
    });
    (0, index_1.initSlippageState)();
    // -----------------------------------------------------------------------
    // 3. Manual price controls
    // -----------------------------------------------------------------------
    (0, index_1.setupManualPriceControls)();
    (0, index_1.initManualPriceState)();
    // -----------------------------------------------------------------------
    // 4. Standard order buttons (two-stage click)
    // -----------------------------------------------------------------------
    // Disarm all armed buttons whenever the selected market changes, so an
    // armed button can never fire on a different market than it was armed on.
    (0, index_1.registerTwoStageMarketGuard)();
    (0, index_1.attachTwoStageClick)(buttons.buyA, "yes", "buy", armTimeoutId, allButtons, () => confirmRequired.value, undefined, "trad_buy_yes");
    (0, index_1.attachTwoStageClick)(buttons.sellA, "yes", "sell", armTimeoutId, allButtons, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(buttons.buyB, "no", "buy", armTimeoutId, allButtons, () => confirmRequired.value, undefined, "trad_buy_no");
    (0, index_1.attachTwoStageClick)(buttons.sellB, "no", "sell", armTimeoutId, allButtons, () => confirmRequired.value);
    // -----------------------------------------------------------------------
    // 5. Sell-mode indicator
    // -----------------------------------------------------------------------
    const initialState = (0, state_1.getState)();
    (0, ui_1.updateSellModeIndicator)(initialState.tradingSettings.sellMode ?? "full", initialState.tradingSettings.fixedSellQuantity ?? 1);
    // -----------------------------------------------------------------------
    // 6. Baseball mode: buy buttons, defense buttons, factor controls, sell
    // -----------------------------------------------------------------------
    (0, index_1.setupBaseballBuyButtons)(bbButtons, allButtons, sportButtons, armTimeoutId, () => confirmRequired.value);
    (0, index_1.setupBaseballDefenseButtons)(bbDefButtons, allButtons, sportButtons, armTimeoutId, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(bbButtons.sellA, "yes", "sell", armTimeoutId, sportButtons, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(bbButtons.sellB, "no", "sell", armTimeoutId, sportButtons, () => confirmRequired.value);
    (0, index_1.setupBaseballFactorControls)(document.getElementById("baseball-factor-slider"), document.getElementById("baseball-factor-decrement"), document.getElementById("baseball-factor-increment"));
    (0, index_1.initBaseballState)();
    (0, index_1.setupButtonPriceSteppers)();
    (0, index_1.setupSetToAskButton)();
    // -----------------------------------------------------------------------
    // 6b. Basketball mode: buy buttons, factor controls (shared factor), sell
    // -----------------------------------------------------------------------
    (0, index_1.setupBasketballBuyButtons)(bkButtons, allButtons, sportButtons, armTimeoutId, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(bkButtons.sellA, "yes", "sell", armTimeoutId, sportButtons, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(bkButtons.sellB, "no", "sell", armTimeoutId, sportButtons, () => confirmRequired.value);
    // The basketball slider/± row drives the same shared factor as baseball's.
    (0, index_1.setupBaseballFactorControls)(document.getElementById("bk-factor-slider"), document.getElementById("bk-factor-decrement"), document.getElementById("bk-factor-increment"));
    (0, index_1.setupBasketballPricingControls)();
    // -----------------------------------------------------------------------
    // 6c. Soccer mode: buy buttons, factor controls (shared factor), sell
    // -----------------------------------------------------------------------
    (0, index_1.setupSoccerBuyButtons)(scButtons, allButtons, sportButtons, armTimeoutId, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(scButtons.sellA, "yes", "sell", armTimeoutId, sportButtons, () => confirmRequired.value);
    (0, index_1.attachTwoStageClick)(scButtons.sellB, "no", "sell", armTimeoutId, sportButtons, () => confirmRequired.value);
    // The soccer slider/± row drives the same shared factor as baseball's.
    (0, index_1.setupBaseballFactorControls)(document.getElementById("sc-factor-slider"), document.getElementById("sc-factor-decrement"), document.getElementById("sc-factor-increment"));
    (0, index_1.setupSoccerPricingControls)();
    // -----------------------------------------------------------------------
    // 7. Ancillary controls
    // -----------------------------------------------------------------------
    (0, index_1.setupEventTypeModal)();
    (0, index_1.setupSettingsInfoTooltip)();
    (0, index_1.setupDollarAmountButtons)();
    (0, index_1.setupInningControls)();
    (0, index_1.setupOffenseControls)();
    // -----------------------------------------------------------------------
    // 8. Additional market assignments
    // -----------------------------------------------------------------------
    (0, additional_markets_service_1.cleanupStaleAssignments)();
    (0, modal_handlers_1.setupAdditionalMarketsModal)();
    (0, modal_handlers_1.setupParallelMarketsModal)();
    (0, modal_handlers_1.updateAdditionalMarketBadges)();
    (0, modal_handlers_1.updateSettingsInfoAdditionalMarkets)();
    // Show/hide clear buttons based on whether assignments exist
    const hasAssignments = (0, additional_markets_service_1.getAdditionalAssignments)().length > 0;
    for (const btnId of modal_handlers_1.CLEAR_ADDITIONAL_MARKETS_BTN_IDS) {
        document.getElementById(btnId)?.classList.toggle("hidden", !hasAssignments);
    }
}
